import { type z } from 'zod';

import { IS_LOCAL } from '../../constants/env';
import { asUnknown, isDefined } from '../../utils';

/**
 * Class that provides parsed extra data with and without defaults
 */
class ExtraDataParser<TWithDefaults, TWithoutDefaults> {
	#withDefaults: TWithDefaults;
	#withoutDefaults: TWithoutDefaults;
	#withDefaultsParsed = false;
	#withoutDefaultsParsed = false;
	#extraData: any;
	#config: {
		schemaWithDefaults: z.ZodType<any, any>;
		schemaWithoutDefaults: z.ZodType<any, any>;
		defaultData: any;
		polyfillFn?: (data: any) => any;
	};
	#options: {
		returnPartialOnError: boolean;
		logErrors: boolean;
	};

	/**
	 * Creates a new ExtraDataParser instance
	 */
	constructor(
		extraData: any,
		config: {
			schemaWithDefaults: z.ZodType<any, any>;
			schemaWithoutDefaults: z.ZodType<any, any>;
			defaultData: any;
			polyfillFn?: (data: any) => any;
		},
		options: {
			returnPartialOnError: boolean;
			logErrors: boolean;
		},
	) {
		this.#extraData = extraData;
		this.#config = config;
		this.#options = options;
		this.#withDefaults = config.defaultData;
		this.#withoutDefaults = config.defaultData;
	}

	/**
	 * Gets the parsed data with default values applied
	 */
	get withDefaults(): TWithDefaults {
		if (!this.#withDefaultsParsed) {
			this.#parseWithDefaults();
		}
		return this.#withDefaults;
	}

	/**
	 * Gets the parsed data without default values
	 */
	get withoutDefaults(): TWithoutDefaults {
		if (!this.#withoutDefaultsParsed) {
			this.#parseWithoutDefaults();
		}
		return this.#withoutDefaults;
	}

	/**
	 * Parses the data with default values
	 */
	#parseWithDefaults(): void {
		try {
			/** return default data if no input provided */
			if (!isDefined(this.#extraData)) {
				this.#withDefaults = this.#config.schemaWithDefaults.parse({});
				this.#withDefaultsParsed = true;
				return;
			}

			/** apply polyfills if provided */
			const dataUnknown = asUnknown<any>(this.#extraData);
			const data = this.#config.polyfillFn ? this.#config.polyfillFn(dataUnknown) : dataUnknown;

			if (!this.#options.returnPartialOnError) {
				/** when returnPartialOnError=false, we don't attempt to preserve any fields on error */
				const withDefaultsRes = this.#config.schemaWithDefaults.safeParse(data);

				if (!withDefaultsRes.success) {
					if (this.#options.logErrors) {
						console.debug('[ExtraDataParser] invalid extra data with defaults, returning default data', {
							error: withDefaultsRes.error?.errors?.[0],
							extraData: this.#extraData,
						});
					}
					/** return default data on any validation error */
					this.#withDefaults = structuredClone(this.#config.defaultData);
				} else {
					this.#withDefaults = withDefaultsRes.data;
				}
			} else {
				/** when returnPartialOnError=true, try to preserve valid fields */
				try {
					/** first attempt full parsing */
					this.#withDefaults = this.#config.schemaWithDefaults.parse(data);
				} catch (error) {
					if (this.#options.logErrors) {
						console.debug('[ExtraDataParser] invalid extra data with defaults, preserving valid fields', {
							error,
							extraData: this.#extraData,
						});
					}

					/** extract and preserve top-level valid fields */
					this.#withDefaults = this.#config.schemaWithDefaults.parse({});

					/** copy over valid top-level fields from input data */
					Object.keys(data).forEach((key) => {
						try {
							const fieldSchema = (this.#config.schemaWithDefaults as any).shape?.[key];
							if (fieldSchema) {
								const parsed = fieldSchema.safeParse(data[key]);
								if (parsed.success) {
									(this.#withDefaults as any)[key] = parsed.data;
								}
							}
						} catch (err) {
							/** ignore errors for individual fields */
						}
					});
				}
			}
		} catch (error) {
			/** fallback to default data on any error */
			if (this.#options.logErrors) {
				console.debug('[ExtraDataParser] Invalid extra data schema with defaults', { error, extraData: this.#extraData });
			}
			this.#withDefaults = structuredClone(this.#config.defaultData);
		}

		this.#withDefaultsParsed = true;
	}

	/**
	 * Parses the data without default values
	 */
	#parseWithoutDefaults(): void {
		try {
			/** return default data if no input provided */
			if (!isDefined(this.#extraData)) {
				this.#withoutDefaults = structuredClone(this.#config.defaultData);
				this.#withoutDefaultsParsed = true;
				return;
			}

			/** apply polyfills if provided */
			const dataUnknown = asUnknown<any>(this.#extraData);
			const data = this.#config.polyfillFn ? this.#config.polyfillFn(dataUnknown) : dataUnknown;

			if (!this.#options.returnPartialOnError) {
				/** when returnPartialOnError=false, we don't attempt to preserve any fields on error */
				const withoutDefaultsRes = this.#config.schemaWithoutDefaults.safeParse(data);

				if (!withoutDefaultsRes.success) {
					if (this.#options.logErrors) {
						console.debug('[ExtraDataParser] invalid extra data without defaults, returning default data', {
							error: withoutDefaultsRes.error?.errors?.[0],
							extraData: this.#extraData,
						});
					}
					/** return default data on any validation error */
					this.#withoutDefaults = structuredClone(this.#config.defaultData);
				} else {
					this.#withoutDefaults = withoutDefaultsRes.data;
				}
			} else {
				/** when returnPartialOnError=true, try to preserve valid fields */
				try {
					/** first attempt full parsing */
					this.#withoutDefaults = this.#config.schemaWithoutDefaults.parse(data);
				} catch (error) {
					if (this.#options.logErrors) {
						console.debug('[ExtraDataParser] invalid extra data without defaults, preserving valid fields', {
							error,
							extraData: this.#extraData,
						});
					}

					/** start with default data */
					this.#withoutDefaults = structuredClone(this.#config.defaultData);

					/** copy over valid top-level fields from input data */
					Object.keys(data).forEach((key) => {
						try {
							const fieldSchema = (this.#config.schemaWithoutDefaults as any).shape?.[key];
							if (fieldSchema) {
								const parsed = fieldSchema.safeParse(data[key]);
								if (parsed.success) {
									(this.#withoutDefaults as any)[key] = parsed.data;
								}
							}
						} catch (err) {
							/** ignore errors for individual fields */
						}
					});
				}
			}
		} catch (error) {
			/** fallback to default data on any error */
			if (this.#options.logErrors) {
				console.debug('[ExtraDataParser] Invalid extra data schema without defaults', { error, extraData: this.#extraData });
			}
			this.#withoutDefaults = structuredClone(this.#config.defaultData);
		}

		this.#withoutDefaultsParsed = true;
	}
}

/**
 * Creates a helper function for parsing extra data with and without default values
 * @param config Configuration object for the helper
 * @returns Function that returns a class with getters for parsed data with and without defaults
 */
export function createGetExtraDataParsedHelper<TWithDefaults extends z.ZodType<any, any>, TWithoutDefaults extends z.ZodType<any, any>>(config: {
	/** Schema with default values applied */
	schemaWithDefaults: TWithDefaults;
	/** Schema without default values */
	schemaWithoutDefaults: TWithoutDefaults;
	/** Default data to use when parsing fails or no data is provided */
	defaultData: z.output<TWithDefaults>;
	/** Optional function to transform data before parsing (for backwards compatibility) */
	polyfillFn?: (data: any) => any;
}): (
	extraData?: object | null,
	options?: { returnPartialOnError?: boolean; logErrors?: boolean },
) => ExtraDataParser<z.output<TWithDefaults>, z.output<TWithoutDefaults>> {
	return (extraData, options) => {
		const { returnPartialOnError = true, logErrors = IS_LOCAL } = options ?? {};

		return new ExtraDataParser<z.output<TWithDefaults>, z.output<TWithoutDefaults>>(extraData, config, { returnPartialOnError, logErrors });
	};
}
